<template>
  <aside id="app-sidebar" class="secudoc-sidebar" :class="{ 'is-open': open }" aria-label="Main navigation">
    <!-- Brand -->
    <div class="sidebar-brand">
      <img src="@/assets/brand/secudoc-horizontal.svg" alt="SecuDoc" class="sidebar-brand__logo" />
    </div>

    <!-- Nav links — rendered based on role -->
    <nav class="sidebar-nav">
      <!-- Role 0 (Admin) links -->
      <template v-if="role === 0">
        <b-link to="/dashboard-admin" class="sidebar-nav__item" active-class="sidebar-nav__item--active">
          <b-icon icon="house-door-fill" font-scale="1.25" aria-hidden="true"/>
          <span>Admin Dashboard</span>
        </b-link>
        <b-link to="/dashboard-org-admin" class="sidebar-nav__item" active-class="sidebar-nav__item--active">
          <b-icon icon="people-fill" font-scale="1.25" aria-hidden="true"/>
          <span>Org Admin Dashboard</span>
        </b-link>
        <b-link to="/create-org-admin" class="sidebar-nav__item" active-class="sidebar-nav__item--active">
          <b-icon icon="person-plus-fill" font-scale="1.25" aria-hidden="true"/>
          <span>Create Org Admin</span>
        </b-link>
      </template>

      <!-- Role 1 (Org Admin) links -->
      <template v-else-if="role === 1">
        <b-link to="/dashboard-org-admin" class="sidebar-nav__item" active-class="sidebar-nav__item--active">
          <b-icon icon="house-door-fill" font-scale="1.25" aria-hidden="true"/>
          <span>Dashboard</span>
        </b-link>
        <b-link to="/create-user" class="sidebar-nav__item" active-class="sidebar-nav__item--active">
          <b-icon icon="person-plus-fill" font-scale="1.25" aria-hidden="true"/>
          <span>Create User</span>
        </b-link>
      </template>

      <!-- Role 2 (Professional) links — also the fallback while role is loading -->
      <template v-else>
        <b-link to="/dashboard-org-professional" class="sidebar-nav__item" active-class="sidebar-nav__item--active">
          <b-icon icon="house-door-fill" font-scale="1.25" aria-hidden="true"/>
          <span>Dashboard</span>
        </b-link>
        <b-link to="/scan" class="sidebar-nav__item" active-class="sidebar-nav__item--active">
          <b-icon icon="grid3x3-gap-fill" font-scale="1.25" aria-hidden="true"/>
          <span>Scan QR</span>
        </b-link>
        <b-link to="/create-document" class="sidebar-nav__item" active-class="sidebar-nav__item--active">
          <b-icon icon="plus-circle-fill" font-scale="1.25" aria-hidden="true"/>
          <span>Create Document</span>
        </b-link>
        <b-link to="/verify-fs-set" class="sidebar-nav__item" active-class="sidebar-nav__item--active">
          <b-icon icon="check-circle-fill" font-scale="1.25" aria-hidden="true"/>
          <span>Verify FS Set</span>
        </b-link>
      </template>
    </nav>

    <!-- Bottom: Profile + Sign Out -->
    <div class="sidebar-footer">
      <b-link to="/profile" class="sidebar-nav__item" active-class="sidebar-nav__item--active">
        <b-icon icon="person-circle" font-scale="1.25" aria-hidden="true"/>
        <span>My Profile</span>
      </b-link>
      <button class="sidebar-nav__item sidebar-signout" @click="signOut">
        <b-icon icon="box-arrow-left" font-scale="1.25" aria-hidden="true"/>
        <span>Sign Out</span>
      </button>
    </div>
  </aside>
</template>

<script>
import firebase from "firebase/compat/app";
import "firebase/compat/auth";
import "firebase/compat/firestore";

export default {
  name: "app-sidebar",
  props: {
    open: { type: Boolean, default: false }
  },
  data() {
    return { role: 2 }  // default to Professional until Firestore confirms
  },
  created() {
    const user = firebase.auth().currentUser;
    if (user) {
      firebase.firestore().collection("Users").doc(user.uid).get()
        .then(snapshot => {
          if (snapshot.exists) {
            const r = snapshot.data().role;
            if (r === 0 || r === 1 || r === 2) this.role = r;
          }
        })
        .catch(err => console.error("sidebar role load:", err));
    }
  },
  methods: {
    signOut() {
      firebase.auth().signOut().then(() => {
        this.$router.replace({ name: "login" });
      }).catch(err => console.error(err));
    }
  }
}
</script>

<style lang="scss" scoped>
.secudoc-sidebar {
  display: flex;
  flex-direction: column;
  width: 220px;
  min-height: 100vh;
  background-color: var(--secudoc-surface);
  border-right: 1px solid var(--secudoc-outline);
  position: sticky;
  top: 0;
  flex-shrink: 0;

  /* Mobile: slide-in drawer */
  @media (max-width: 767.98px) {
    position: fixed;
    top: 0;
    left: 0;
    height: 100%;
    z-index: 1040;
    transform: translateX(-100%);
    transition: transform 220ms cubic-bezier(0.4, 0, 0.2, 1);
    box-shadow: none;

    &.is-open {
      transform: translateX(0);
      box-shadow: 4px 0 16px rgba(0, 0, 0, 0.18);
    }
  }
}

.sidebar-brand {
  height: 60px;
  display: flex;
  align-items: center;
  padding: 0 var(--secudoc-space-4);
  border-bottom: 1px solid var(--secudoc-outline);
  margin-bottom: var(--secudoc-space-3);
  flex-shrink: 0;
}

.sidebar-brand__logo {
  height: 32px;
  width: auto;
}

.sidebar-nav {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: var(--secudoc-space-1);
  padding: 0 var(--secudoc-space-3);
}

.sidebar-nav__item {
  display: flex;
  align-items: center;
  gap: var(--secudoc-space-3);
  padding: var(--secudoc-space-2) var(--secudoc-space-3);
  border-radius: var(--secudoc-radius-md);
  color: var(--secudoc-on-surface-variant);
  font-size: 0.9375rem;
  font-family: var(--secudoc-font-body);
  text-decoration: none;
  transition: background 120ms ease, color 120ms ease;
  background: transparent;
  border: none;
  width: 100%;
  cursor: pointer;
  text-align: left;
  min-height: 44px;

  &:hover,
  &:focus {
    background-color: var(--secudoc-surface-variant);
    color: var(--secudoc-on-surface);
    text-decoration: none;
  }

  &.sidebar-nav__item--active {
    background-color: var(--secudoc-primary-container, rgba(13, 43, 78, 0.12));
    color: var(--secudoc-primary);
    font-weight: 600;
  }

  /* ensure Bootstrap-Vue SVG icons render at a consistent size */
  ::v-deep .b-icon {
    font-size: 1.1rem;
    flex-shrink: 0;
    width: 1.1rem;
    height: 1.1rem;
  }
}

.sidebar-footer {
  padding: var(--secudoc-space-3);
  border-top: 1px solid var(--secudoc-outline);
  margin-top: var(--secudoc-space-3);
  display: flex;
  flex-direction: column;
  gap: var(--secudoc-space-1);
}

.sidebar-signout {
  color: var(--secudoc-error);

  &:hover,
  &:focus {
    background-color: rgba(179, 38, 30, 0.1); /* 10% of --secudoc-error (#B3261E) */
    color: var(--secudoc-error);
  }
}
</style>
